import type {
  PageVersion,
  PageVersionsResponse,
} from '@skupreme/api-contracts/core';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/auth';
import { BaseService } from '@utility/services/base';
import { APIPath } from '@utility/constants/api';
import { readPageView } from '@utility/page-view';

const NONE: PageVersionsResponse['pages'] = {
  orders: false,
  catalog: false,
  storage_providers: false,
  wallet: false,
};
const ALL: PageVersionsResponse['pages'] = {
  orders: true,
  catalog: true,
  storage_providers: true,
  wallet: true,
};

/*
 * EN-4134: which pages the current division reads from V2. A V2 account sees
 * only what V2 has data for; everything else is hidden. Every account is on V2
 * now, so until the answer comes every page is V2 (no flash of the legacy page
 * first); when the call fails, nothing is V2 and nothing hides.
 */
export function usePageVersions() {
  const { user } = useAuth() as {
    user?: { _id?: string; organization_id?: string };
  };
  const query = useQuery<PageVersionsResponse>({
    queryKey: ['page-versions', user?._id, user?.organization_id],
    queryFn: () => BaseService.get(APIPath.pageVersions),
    enabled: !!user?._id,
    retry: false,
    staleTime: 5 * 60_000,
  });
  /* An answer that names no pages (an older API) is nothing V2, as a failure is. */
  const pages = query.data
    ? (query.data.pages ?? NONE)
    : query.isError
      ? NONE
      : ALL;
  const legacyView = readPageView() === 'legacy';

  return {
    /** The account's switches, whatever view a superadmin picked. */
    pages,
    /** False until the API answered (or failed): `isV2` is then only the default. */
    known: query.data !== undefined || query.isError,
    /** True when the account has any V2 page: the picker shows. */
    hasV2: Object.values(pages).some(Boolean),
    /** The page is served from V2 now (a superadmin's legacy view says no). */
    isV2: (page: PageVersion) => pages[page] && !legacyView,
  };
}

/** The account runs on the new system: the orders switch, as the rebuild settings read it. */
export function useIsV2Account() {
  return usePageVersions().isV2('orders');
}
